import { MetricDistributionRange, MetricDistributionPercentiles } from '@statsbomb/parachute-types';
import { keepPreviousData } from '@tanstack/react-query';
import { atom } from 'jotai';
import { selectAtom, unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { unwrappedMetricLookupMapAtom } from '@/atoms/queries/metric';
import { unwrappedGenderAtom } from '@/atoms/queries/userConfigs';
import { metricDistributionPercentilesUrl } from '@/query/url';
import { MetricLookupMap } from '@/types/metric';
import { getMetricDistributionsParams, formatMetricDistributions, getPhysicalMetricPrecision } from '@/utils/metrics';
import { objSnakeToCamel } from '@/utils/queries';
import { groupTableColumns } from '@/utils/table';
import { filtersAtom } from '../filters/filter';
import { enrichedTableColumnsAtom, unwrappedTableColumnsAtom } from '../queries/tableColumns';

type FetchFn = (input: RequestInfo, init?: RequestInit) => Promise<any>;

export const normalisedToRawMetricKeys = (
  normalisedMetricNames: string[],
  metricLookupMap: MetricLookupMap | undefined,
): string[] =>
  normalisedMetricNames.flatMap(normalisedMetricName => {
    const normalisedKey = normalisedMetricName.replace('aggregates.', '');
    const rawKey = metricLookupMap?.get(normalisedKey)?.key;
    // metrics absent from the lookup map are silently dropped — API only accepts raw keys
    return rawKey !== undefined ? [rawKey] : [];
  });

// Exported so the atom and the prefetch hook build identical query keys, guaranteeing cache hits on Apply
export const buildPlayerDataLockerMetricDistributionQueryOptions = (
  gender: string | undefined,
  rawMetricKeys: string[],
  fetchFn: FetchFn,
  isMetricLookupResolved = true,
) => {
  // A pending lookup is keyed like a missing gender, so both resolve to `null` (not ready) in the queryFn
  const queryKey = [
    'playerDataLockerMetricDistributions',
    isMetricLookupResolved ? gender : undefined,
    rawMetricKeys,
  ] as const;

  // `null`: not ready (no gender, or the metric lookup is pending). `[]`: ready, but nothing to request.
  const queryFn = async ({
    queryKey: [, keyGender, metrics],
  }: {
    queryKey: typeof queryKey;
  }): Promise<MetricDistributionPercentiles[] | null> => {
    if (!keyGender) return null;
    if (!metrics.length) return [];
    const params = { eq: { gender: keyGender, positionGroup: 'all' }, in: { metrics } };
    const url = metricDistributionPercentilesUrl('players', params);
    const data: MetricDistributionPercentiles[] = await fetchFn(url);
    return data;
  };

  return { queryKey, queryFn };
};

export const selectedMetricDistributionsAtom = atom(
  get => get(filtersAtom).metricDistributions,
  (get, set, next: MetricDistributionRange[]) => {
    const filters = get(filtersAtom);
    set(filtersAtom, { ...filters, metricDistributions: next });
  },
);

export const stableSelectedNormalisedMetricNamesAtom = selectAtom(
  selectedMetricDistributionsAtom,
  distributions => distributions.map(({ name }) => name),
  (a, b) => a.length === b.length && a.every((name, i) => name === b[i]),
);

export const selectedMetricsAtom = atom(
  get => {
    const selectedMetricDistributions = get(selectedMetricDistributionsAtom);
    const selectedMetrics = selectedMetricDistributions.map(({ name }) => name);

    return selectedMetrics;
  },
  (get, set, metrics: string[]) => {
    const selectedMetricDistributions = get(selectedMetricDistributionsAtom);

    const newSelectedMetricDistributions = metrics.map(metric => {
      const existingMetric = selectedMetricDistributions.find(({ name }) => name === metric);

      return existingMetric || { name: metric, min: undefined, max: undefined };
    });

    set(selectedMetricDistributionsAtom, newSelectedMetricDistributions);
  },
);

export const metricDistributionParamsAtom = atom(get => {
  const selectedMetricDistributions = get(selectedMetricDistributionsAtom);
  const metricDistributionParams = getMetricDistributionsParams(selectedMetricDistributions);

  return metricDistributionParams;
});

export const playerMetricGroupsAtom = atom(async get => {
  const enrichedTableColumns = await get(enrichedTableColumnsAtom);

  const playerMetricTableColumns = enrichedTableColumns
    .filter(column => {
      const categoryPath = column.category_path || [];
      const entity = categoryPath[0];
      const category = categoryPath[1] ?? categoryPath[0];

      return entity?.key === 'player_metric' && category?.key !== 'player_player_metric';
    })
    .map(objSnakeToCamel);

  const playerMetricGroups = groupTableColumns(playerMetricTableColumns);

  return playerMetricGroups;
});

const rawPlayerDataLockerMetricDistributionsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const gender = get(unwrappedGenderAtom);
  const normalisedMetricNames = get(stableSelectedNormalisedMetricNamesAtom);
  const metricLookupMap = get(unwrappedMetricLookupMapAtom);

  const rawMetricKeys = normalisedToRawMetricKeys(normalisedMetricNames, metricLookupMap);
  const { queryKey, queryFn } = buildPlayerDataLockerMetricDistributionQueryOptions(
    gender,
    rawMetricKeys,
    fetch,
    metricLookupMap !== undefined,
  );

  // keep the previous metric set's distributions on screen while a changed set is fetched
  return { queryKey, queryFn, placeholderData: keepPreviousData };
});

// jotai-tanstack-query types a suspense query's result without `isPlaceholderData`, but the property is there at
// runtime once `placeholderData` is configured, which is how a query reports that it is showing the previous key's data
const isPlaceholderData = (queryResult: object) =>
  (queryResult as { isPlaceholderData?: boolean }).isPlaceholderData === true;

export const playerMetricDistributionDefinitionsAtom = atom(async get => {
  const normalisedMetricNames = get(stableSelectedNormalisedMetricNamesAtom);
  const metricLookupMap = get(unwrappedMetricLookupMapAtom);
  // not awaited: until the table's columns arrive, or where no table is mounted, the range heuristic applies
  const tableColumns = get(unwrappedTableColumnsAtom);
  const rawPercentiles = await get(rawPlayerDataLockerMetricDistributionsAtom);

  // data is null only while gender or the metric lookup are unresolved: nothing to show yet, and not an error
  if (rawPercentiles.data === null) return undefined;

  return {
    // a physical metric's slider takes its precision from its table column, so the slider and the table agree
    definitions: formatMetricDistributions(rawPercentiles, metricKeyNormalised =>
      getPhysicalMetricPrecision(metricKeyNormalised, metricLookupMap, tableColumns),
    ),
    // undefined while the query shows the previous selection's definitions (keepPreviousData); a background
    // refetch of the same selection stays current.
    resolvedForMetricNames: isPlaceholderData(rawPercentiles) ? undefined : normalisedMetricNames,
  };
});

// `undefined` until the first resolve, so "still loading" differs from "loaded, but this metric has no distribution"
// (a real error). Holds the previous resolved value during a refetch, so Suspense doesn't flash.
const stablePlayerMetricDistributionDefinitionsAtom = unwrap(playerMetricDistributionDefinitionsAtom, prev => prev);

/**
 * The distributions for the current selection, `undefined` until the first resolve. `isCurrent` is false while the
 * held definitions belong to an earlier selection, so a metric missing from them is only a gap once it is true.
 *
 * Synchronous so it compares against the live selection on every read, by reference, which
 * `stableSelectedNormalisedMetricNamesAtom` keeps stable while the names are unchanged.
 */
export const currentPlayerMetricDistributionsAtom = atom(get => {
  const held = get(stablePlayerMetricDistributionDefinitionsAtom);
  if (held === undefined) return undefined;

  return {
    definitions: held.definitions,
    isCurrent: held.resolvedForMetricNames === get(stableSelectedNormalisedMetricNamesAtom),
  };
});
